import { NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';
import {
  User,
  Shield,
  Monitor,
  SlidersHorizontal,
  Bell,
  AlertTriangle,
  CreditCard,
  Receipt,
  BarChart3,
  Package,
  Building2,
  Users,
  UserCheck,
  Mail,
  Lock,
  Activity,
  ShieldCheck,
  Wallet,
  Inbox,
} from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import type { Permission } from '../../../lib/permissions';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  /** If set, the group is only shown when this permission is held */
  requirePermission?: Permission;
  /** If true, the group is only shown to system admins */
  requireSystemAdmin?: boolean;
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Personal',
    items: [
      { to: '/settings/profile',       label: 'Profile',       icon: User },
      { to: '/settings/security',      label: 'Security',      icon: Shield },
      { to: '/settings/sessions',      label: 'Sessions',      icon: Monitor },
      { to: '/settings/preferences',   label: 'Preferences',   icon: SlidersHorizontal },
      { to: '/settings/notifications', label: 'Notifications', icon: Bell },
      { to: '/settings/wallet',        label: 'Wallet',        icon: Wallet },
    ],
  },
  {
    label: 'Workspace',
    requirePermission: 'billing.view',
    items: [
      { to: '/settings/billing',      label: 'Billing',      icon: CreditCard },
      { to: '/settings/subscription', label: 'Subscription', icon: Package },
      { to: '/settings/invoices',     label: 'Invoices',     icon: Receipt },
      { to: '/settings/usage',        label: 'Usage',        icon: BarChart3 },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/settings/account', label: 'Account', icon: AlertTriangle },
    ],
  },
  {
    label: 'Organization',
    requirePermission: 'organization.update',
    items: [
      { to: '/settings/admin',             label: 'Organization',    icon: Building2 },
      { to: '/settings/admin/members',       label: 'Members',         icon: Users },
      { to: '/settings/admin/join-requests', label: 'Join Requests',   icon: UserCheck },
      { to: '/settings/admin/invitations',   label: 'Invitations',     icon: Mail },
      { to: '/settings/admin/security',    label: 'Security',        icon: ShieldCheck },
      { to: '/settings/admin/audit',       label: 'Audit Activity',  icon: Activity },
    ],
  },
  {
    label: 'System',
    requireSystemAdmin: true,
    items: [
      { to: '/settings/system',                 label: 'Organizations',       icon: Building2 },
      { to: '/settings/system/access-requests', label: 'Access Requests',     icon: Inbox },
      { to: '/settings/system/roles',           label: 'Roles & Permissions', icon: Lock },
      { to: '/settings/system/security',        label: 'Security',            icon: ShieldCheck },
    ],
  },
];

export function UserSettingsLayout() {
  const { can, isSystemAdmin } = usePermissions();

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center h-10 px-4 border-b border-surface-4 bg-surface-1 flex-shrink-0">
        <h1 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Settings</h1>
      </div>
      <div className="flex flex-1 overflow-hidden">
        {/* Left nav */}
        <nav
          className="w-52 flex-shrink-0 border-r border-surface-4 bg-surface-1 py-4 px-2 overflow-y-auto"
          aria-label="Settings navigation"
        >
          {NAV_GROUPS.map((group) => {
            if (group.requirePermission && !can(group.requirePermission)) return null;
            if (group.requireSystemAdmin && !isSystemAdmin) return null;

            return (
              <div key={group.label} className="mb-4">
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-600 select-none">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map(({ to, label, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        clsx(
                          'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-brand/15 text-brand'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-surface-3',
                        )
                      }
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
